/*
  Auth API wrapper. Uses the shared axios instance (which carries withCredentials=true
  and X-Client-Id). On any 401 response to an endpoint other than /auth/*, we
  transparently try POST /auth/refresh once before giving up, so short-lived
  access tokens don't boot the user out mid-session.
*/
import { api } from "./api";

let refreshInFlight = null;

async function tryRefresh() {
  if (!refreshInFlight) {
    refreshInFlight = api.post("/auth/refresh").finally(() => { refreshInFlight = null; });
  }
  try { await refreshInFlight; return true; } catch { return false; }
}

// Attach a single response interceptor (idempotent via the __vh_auth marker)
if (!api.__vh_auth_installed) {
  api.__vh_auth_installed = true;
  api.interceptors.response.use(
    (r) => r,
    async (err) => {
      const cfg = err?.config || {};
      const status = err?.response?.status;
      const url = cfg.url || "";
      const isAuthPath = url.includes("/auth/login") || url.includes("/auth/register")
        || url.includes("/auth/refresh") || url.includes("/auth/logout");
      if (status === 401 && !cfg.__retry && !isAuthPath) {
        cfg.__retry = true;
        const ok = await tryRefresh();
        if (ok) return api.request(cfg);
      }
      return Promise.reject(err);
    }
  );
}

export const auth = {
  me: () => api.get("/auth/me").then(r => r.data),
  login: (email, password) => api.post("/auth/login", { email, password }).then(r => r.data),
  register: (email, password, name) => api.post("/auth/register", { email, password, name }).then(r => r.data),
  logout: () => api.post("/auth/logout").then(r => r.data),
  forgot: (email) => api.post("/auth/forgot-password", { email }).then(r => r.data),
  reset: (token, new_password) => api.post("/auth/reset-password", { token, new_password }).then(r => r.data),
  migrateClient: () => api.post("/auth/migrate-client").then(r => r.data),
};
